import isNull from "lodash/isNull";
import * as React from "react";
import { observer } from "mobx-react";
import { Spring, animated } from "react-spring/renderprops.cjs";
import { reaction } from "mobx";

import { useDragWithoutPreview } from "util/react-utils";
import { DND_SECTION } from "pages/editor/lib/timeline";

import { undoManager } from "pages/editor/state/store/undomanager";

import { ConnectionEl } from "./section.styles";
import Section from "./section";

const SECTION_Z_INDEX = 1;
const TOP_Z_INDEX = 100;

const parseSectionItem = (ui, section) => ({
    id: section.id,
    type: section.asset.type,
    initialIndex: section.index,
    isChildOfBaseLayer: section.isChildOfBaseLayer,
    isConstrainedToBaseLayer: section.isConstrainedToBaseLayer,
    ...(ui.composition.baseLayer && {
        baseLayerPreview: [...ui.composition.baseLayer.sectionsArraySorted]
    })
});

const TimelineSection = ({ ui, section }) => {
    const { x, y, width, height, zIndex, showConnection, connectionHeight } =
        section;

    const [{ isDragging, monitor }, drag] = useDragWithoutPreview({
        type: DND_SECTION,
        item: () => {
            ui.onSectionDragStart(section.id);
            return parseSectionItem(ui, section);
        },
        end: () => {
            ui.onSectionDragEnd();
            if (ui.composition.baseLayer) {
                ui.composition.sortBaseLayer();
            }
            undoManager.stopGroup();
        },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
            monitor
        })
    });

    const stateRef = React.useRef({});

    const setWasPartOfDragPreview = (val) =>
        (stateRef.current.wasPartOfDragPreview = val);

    const positionUpdateFrame = (callback) =>
        (stateRef.current.requestAnimationFrameId =
            window.requestAnimationFrame(callback));

    const cancelPositionUpdateFrame = () => {
        window.cancelAnimationFrame(stateRef.current.requestAnimationFrameId);
        delete stateRef.current.requestAnimationFrameId;
    };

    const wasPartOfDragPreview = () => stateRef.current.wasPartOfDragPreview;

    const shouldBringToFront = () =>
        section.isPartOfDragPreview || wasPartOfDragPreview();

    const isUpdatingPosition = () => !!stateRef.current.requestAnimationFrameId;

    /**
     * Effect to handle the update of section position during a drag.
     * If a section is dragged we update the drag state in the ui store with the help of an RAF loop.
     */
    React.useEffect(() => {
        const onPositionUpdate = () => {
            const offset = monitor.getDifferenceFromInitialOffset();
            !isNull(offset) && ui.setDragOffset(offset);
            positionUpdateFrame(onPositionUpdate);
        };

        if (isDragging && !isUpdatingPosition()) {
            positionUpdateFrame(onPositionUpdate);
        }

        if (!isDragging && isUpdatingPosition()) {
            cancelPositionUpdateFrame();
            ui.setDragOffset(null);
        }
    }, [isDragging]);

    /**
     * Register whether a section was part of a drag preview. In the case that it was, we want to animate it back to its position.
     */
    React.useEffect(() => {
        return reaction(
            () => section.isPartOfDragPreview,
            (curr, prev) => {
                if (prev && !curr) {
                    setWasPartOfDragPreview(true);
                }
            }
        );
    }, []);

    const zIndexModifier = shouldBringToFront() ? TOP_Z_INDEX : SECTION_Z_INDEX;

    return (
        <Spring
            native
            to={{ xy: [x, y], height }}
            config={{
                mass: 1,
                friction: 75,
                tension: 1000
            }}
            immediate={!(section.animated || wasPartOfDragPreview())}
            onRest={
                wasPartOfDragPreview()
                    ? () => setWasPartOfDragPreview(false)
                    : null
            }
        >
            {({ xy, height }) => (
                <animated.div
                    style={{
                        transform: xy.interpolate(
                            (x, y) => `translate3d(${x}px, ${y}px, 0px)`
                        ),
                        height,
                        position: `absolute`,
                        zIndex: zIndex + zIndexModifier,
                        width: `${width}px`
                    }}
                    data-testid={`timeline-section-${section.id}`}
                    ref={drag}
                >
                    {showConnection && (
                        <ConnectionEl
                            height={connectionHeight}
                            section={section}
                        />
                    )}
                    <Section ui={ui} section={section} />
                </animated.div>
            )}
        </Spring>
    );
};

export default observer(TimelineSection);
